import type { ComponentType } from 'react';

/**
 * Stale-chunk recovery for lazy routes.
 *
 * Every page is a lazy `import()`. After a deploy rotates the hashed chunk
 * filenames, a browser still holding a cached `index.html` requests the *old*
 * chunk URL. Our static host answers a missing `/assets/*.js` with `200
 * text/html` (the SPA-fallback `index.html`), so `import()` receives HTML and
 * throws "Failed to fetch dynamically imported module". Without recovery React
 * Router shows its raw default error page (see bug 110529).
 *
 * A single hard reload fetches the fresh `index.html` (and thus the current
 * chunk URLs), so the common case self-heals. A `sessionStorage` guard makes
 * the reload fire at most once, so a chunk that is genuinely unreachable falls
 * through to the route `errorElement` instead of looping.
 */

const RELOAD_KEY = 'route-chunk-reloaded';

/** Matches the dynamic-import failure messages across browsers (Chrome/Firefox/Safari). */
export function isDynamicImportError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|dynamically imported module/i.test(
    message,
  );
}

/** Side effects behind a seam so tests can observe them without touching real globals. */
export const routeReload = {
  hasReloaded(): boolean {
    try {
      return sessionStorage.getItem(RELOAD_KEY) != null;
    } catch {
      return false;
    }
  },
  markReloaded(): void {
    try {
      sessionStorage.setItem(RELOAD_KEY, '1');
    } catch {
      /* private mode / storage disabled — best effort */
    }
  },
  clear(): void {
    try {
      sessionStorage.removeItem(RELOAD_KEY);
    } catch {
      /* ignore */
    }
  },
  reload(): void {
    window.location.reload();
  },
};

/**
 * Reload once on a stale-chunk import error; otherwise rethrow so the error
 * reaches the route `errorElement`. Returns `true` when a reload was scheduled
 * (caller should stop rendering), `false` is never returned — it either
 * schedules a reload or throws.
 */
export function recoverFromImportError(error: unknown): void {
  if (isDynamicImportError(error) && !routeReload.hasReloaded()) {
    routeReload.markReloaded();
    routeReload.reload();
    return;
  }
  throw error;
}

/**
 * Build a React Router `lazy` loader that resolves to `{ Component }` and
 * recovers from stale-chunk failures. Drop-in replacement for the inline
 * `async () => ({ Component: (await import(...)).X })` pattern.
 */
export function lazyRoute<K extends string>(
  loader: () => Promise<Record<K, ComponentType>>,
  key: K,
) {
  return async (): Promise<{ Component: ComponentType }> => {
    try {
      const module = await loader();
      routeReload.clear();
      return { Component: module[key] };
    } catch (error) {
      recoverFromImportError(error);
      // A reload was scheduled; block rendering until the navigation happens so
      // React Router doesn't flash the errorElement mid-reload.
      return new Promise<{ Component: ComponentType }>(() => {});
    }
  };
}
